import React, { useEffect, useState } from 'react'
import { Button, Input, Select, Space, Table, Typography } from 'antd';
import { requestTypeOption } from 'modules/utilities/utility';
import Address from './Address';
import _ from 'lodash';
const { Text } = Typography;
// requestTypeLabel is additive: every existing caller omits it and keeps
// today's exact text. "Replacement" is also the only value ever sent as
// request_type (see requestTypeOption in utility.js) — this only changes
// what the agent reads, not what the backend receives, since this component
// was originally written for missing/damaged/mismatched product tickets
// (requestTypeSubcategories) where a replacement genuinely is what happens.
// Smart Ticket Creation's Return Request / Return Request (COD) are a pure
// refund with no replacement shipped, so the unconditional "Replacement"
// wording was misleading there.
const ProductCalculation = ({ order, shipmentAddress, setShipmentAddress, setTicketPayload, setValidation, overrideProducts, requestTypeLabel = 'Replacement (Only option available)' }) => {
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedProductData, setSelectedProductData] = useState([]);
    const [productTable, setProductTable] = useState([]);
    const [requestType, setRequestType] = useState('Replacement');
    const orderOptions = overrideProducts || order.order_meta.line_items.map((option) => ({
        value: option?.variant_id,
        label: option?.name,
    }))
    useEffect(() => {
        // Set default request type on mount
        setTicketPayload(prevPayload => ({ ...prevPayload, request_type: 'Replacement' }))
        setValidation(prevValidation => ({ ...prevValidation, products: false, request_type: true }))

        // Cleanup on unmount
        return () => {
            setTicketPayload(prevPayload => {
                const { request_type, ...rest } = prevPayload;
                return rest;
            })
            setValidation(prevValidation => {
                const { request_type, ...rest } = prevValidation;
                return rest;
            })
        }
    }, [])
    useEffect(() => {
        const productsToReturn = Object.values(selectedProductData).filter(product => product.quantity);
        const totalRefundAmount = productsToReturn.reduce((acc, product) => {
            return acc + (+product.price * +product.quantityToRefund);
        }, 0);
        setValidation(prevValidation => ({ ...prevValidation, products: (_.size(productsToReturn) === _.size(selectedProducts) && _.size(productsToReturn) > 0) ? true : false }))
        setTicketPayload(prevPayload => ({ ...prevPayload, products: productsToReturn, total_refund_price: totalRefundAmount.toFixed(2) }))
    }, [selectedProductData])

    useEffect(() => {
        const data = selectedProducts?.map(product => (
            {
                key: product.product_id || product.variant_id,
                name: product.name,
                quantity: product.quantity,
                refundQuantity: product.refundQuantity,
                price: product.price,
                addQuantity: <Counter
                    min={0}
                    setProducts={setProducts}
                    variantId={product.variant_id}
                    selectedProducts={selectedProducts}
                    setSelectedProductData={setSelectedProductData}
                    max={+product.quantity - +product.refundQuantity}
                />
            }
        ))
        setProductTable(data);
    }, [selectedProducts, setSelectedProductData])

    const handleChange = (selectedOptions) => {
        let selectedProductData;
        if (overrideProducts) {
            selectedProductData = overrideProducts
                .filter(item => selectedOptions.includes(item.value))
                .map((product) => ({
                    price: product.price || "",
                    product_id: product.product_id || product.value,
                    variant_id: product.variant_id || product.value,
                    name: product.label,
                    quantity: 1,
                    refundQuantity: 0,
                }));
        } else {
            selectedProductData = order.order_meta.line_items.filter(item => selectedOptions.includes(item.variant_id))
                .map((product) => ({
                    price: product.price || "",
                    product_id: product.product_id || "",
                    variant_id: product.variant_id || "",
                    name: product.name || "",
                    quantity: product.quantity,
                    refundQuantity: isNaN(product?.refundQuantity) ? 0 : Number(product?.refundQuantity),
                }));
        }
        setSelectedProducts(selectedProductData);
        setSelectedProductData(selectedProductData.map(product => ({ ...product, quantity: 0 })).reduce((acc, product) => {
            acc[product.variant_id] = { ...product, quantityToRefund: product.quantityToRefund || 0 };
            return acc;
        }, {}));
    };

    const columns = [
        { title: 'Name', dataIndex: 'name', key: 'name' },
        { title: 'quantity', dataIndex: 'quantity', key: 'quantity' },
        { title: 'Price', dataIndex: 'price', key: 'price' },
        { title: 'Refunded', dataIndex: 'refundQuantity', key: 'refundQuantity' },
        { title: 'To Refund', dataIndex: 'addQuantity', key: 'addQuantity' },
    ]
    return (
        <div>
            <Space.Compact block className='my-3'>
                <span
                    style={{ background: '#FAFAFA', border: '#D9D9D9', width: '30%' }}
                    className='border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2'
                >
                    <Text>Select Products</Text>
                </span>
                <Select
                    style={{ width: '70%' }}
                    placement='topRight'
                    allowClear
                    {...(!overrideProducts ? { mode: "multiple" } : {})}
                    size='middle'
                    placeholder="Please select product"
                    onChange={(val) => handleChange(overrideProducts ? (val ? [val] : []) : val)}
                    options={orderOptions}
                />
            </Space.Compact>
            <Table
                className='my-2'
                pagination={false}
                columns={columns}
                dataSource={productTable}
                size="small"
            />
            <Space.Compact block className='my-3'>
                <span
                    style={{ background: '#FAFAFA', border: '#D9D9D9', width: '30%' }}
                    className='border border-1 border-end-0 d-flex justify-conent-center align-items-center rounded-lg rounded-start px-2'
                >
                    <Text>Return Type</Text>
                </span>
                <div
                    style={{ width: '70%', background: '#FAFAFA', border: '1px solid #D9D9D9' }}
                    className='border-start-0 d-flex align-items-center px-3 py-1'
                >
                    <Text type="secondary">{requestTypeLabel}</Text>
                </div>
            </Space.Compact>
            {requestType === 'Replacement' && <Address shipmentAddress={shipmentAddress} setShipmentAddress={setShipmentAddress} />}
        </div>
    )
}
const Counter = ({ max, min, variantId, setSelectedProductData }) => {
    const [count, setCount] = useState(0)

    useEffect(() => {
        setSelectedProductData(prevData => {
            const data = prevData;
            data[variantId].quantity = count;
            return { ...prevData, ...data };
        })
    }, [count])

    return <Space>
        <Space.Compact>
            <Button disabled={count === min || count < 1} onClick={() => { setCount(prev => prev - 1) }}>-</Button>
            <Input value={count} style={{ width: '2.5rem' }} />
            <Button disabled={count === max} onClick={() => { setCount(prev => prev + 1) }}>+</Button>
        </Space.Compact>
    </Space>
}
export default ProductCalculation